import {
	AGREEMENT_REQUIREMENT_TYPES, 
	AGREEMENT_INTERACTION_TYPES,
	VALID_INTERACTIONS_FOR_REQUIREMENT 
} from "../constants";
import { ACCOUNT_CAMPAIGN_INTERACT } from "../routes";

const REQUIRED_AGREEMENT_TYPES = [
	AGREEMENT_REQUIREMENT_TYPES.AFFIRM,
	AGREEMENT_REQUIREMENT_TYPES.SIGN
];

function getRequirementCodes(version) {
	return version?.Requirements?.map(r => r.Requirement.Code) ?? [];
}

function isRequiredRequirement(requirementCode) {
	return REQUIRED_AGREEMENT_TYPES.indexOf(requirementCode) >= 0;
}

function pickBestContentAvailable(version) {
	if(version.Contents.length === 1) {
		return version.Contents[0];
	}

	const htmlContent = version.Contents.find(c => c.ContentType.Code.toLowerCase() === "text/html");
	if(htmlContent) {
		return htmlContent;
	}

	const markdownContent = version.Contents.find(c => c.ContentType.Code.toLowerCase() === "text/markdown");
	if(markdownContent) {
		return markdownContent;
	}

	const plainTextContent = version.Contents.find(c => c.ContentType.Code.toLowerCase() === "text/plain");
	if(plainTextContent) {
		return plainTextContent;
	}

	return "";
}

function mapCampaignVersionToAgreement(version, interactions = []) {
	const requirementCodes = getRequirementCodes(version);
	const isAccepted = interactions.some(i => VALID_INTERACTIONS_FOR_REQUIREMENT[i.Requirement.Code][i.Interaction.Code] === true);

	return {
		AgreementVersion: version,
		Occurred: version.BeginInclusive,
		Content: pickBestContentAvailable(version),
		RequirementCodes: requirementCodes,
		IsRequired: requirementCodes.some(isRequiredRequirement),
		IsAccepted: isAccepted
	};
}

function AgreementService(
	$logger,
	campaignService,
	api,
	$uibModal
) {
	const service = this;
	service.agreements = [];
	service.hasRequiredAgreements = false;

	service.affirmAgreement = async(agreementId) => {
		const { thisAgreement, campaignId } = getAgreementVersionById(agreementId);
		
		const requiresAffirmInteraction = thisAgreement.RequirementCodes.some(code => code === AGREEMENT_REQUIREMENT_TYPES.AFFIRM);
		if(!requiresAffirmInteraction) {
			return;
		}

		const affirmPayload = {
			Requirement: { Code: AGREEMENT_REQUIREMENT_TYPES.AFFIRM },
			Interaction: { Code: AGREEMENT_INTERACTION_TYPES.ACCEPT }
		};

		await api.post(ACCOUNT_CAMPAIGN_INTERACT(campaignId, agreementId), affirmPayload);

		markAgreementAccepted(agreementId);

		return service.agreements;
	};

	service.signAgreement = async(agreementId) => {
		const { thisAgreement, campaignId } = getAgreementVersionById(agreementId);
		
		const requiresSignInteraction = thisAgreement.RequirementCodes.some(code => code === AGREEMENT_REQUIREMENT_TYPES.SIGN);
		if(!requiresSignInteraction) {
			return;
		}

		const signPayload = {
			Requirement: { Code: AGREEMENT_REQUIREMENT_TYPES.SIGN },
			Interaction: { Code: AGREEMENT_INTERACTION_TYPES.SIGN }
		};

		await api.post(ACCOUNT_CAMPAIGN_INTERACT(campaignId, agreementId), signPayload);

		markAgreementAccepted(agreementId);

		return service.agreements;
	};

	service.rejectAgreement = async(agreementId) => {
		const { thisAgreement, campaignId } = getAgreementVersionById(agreementId);
		
		const requiresAffirmInteraction = thisAgreement.RequirementCodes.some(code => code === AGREEMENT_REQUIREMENT_TYPES.AFFIRM);
		const requiresSignInteraction = thisAgreement.RequirementCodes.some(code => code === AGREEMENT_REQUIREMENT_TYPES.SIGN);
		if(!requiresAffirmInteraction && !requiresSignInteraction) {
			return;
		}

		const Requirement = requiresSignInteraction ? { Code: AGREEMENT_REQUIREMENT_TYPES.SIGN } : { Code: AGREEMENT_REQUIREMENT_TYPES.AFFIRM };

		const affirmPayload = {
			Requirement,
			Interaction: { Code: AGREEMENT_INTERACTION_TYPES.REJECT }
		};

		await api.post(ACCOUNT_CAMPAIGN_INTERACT(campaignId, agreementId), affirmPayload);

		markAgreementAccepted(agreementId);

		return service.agreements;
	};

	service.viewAgreement = async(agreementId) => {
		const { thisAgreement, campaignId } = getAgreementVersionById(agreementId);

		const requiresShowInteraction = thisAgreement.RequirementCodes.some(code => code === AGREEMENT_REQUIREMENT_TYPES.SHOW);
		if(!requiresShowInteraction) {
			return;
		}

		const showPayload = {
			Requirement: { Code: AGREEMENT_REQUIREMENT_TYPES.SHOW },
			Interaction: { Code: AGREEMENT_INTERACTION_TYPES.READ }
		};

		const result = await api.post(ACCOUNT_CAMPAIGN_INTERACT(campaignId, agreementId), showPayload);

		return result;
	};

	service.dismissAgreement = async(agreementId) => {
		const { thisAgreement, campaignId } = getAgreementVersionById(agreementId);

		const requiresShowInteraction = thisAgreement.RequirementCodes.some(code => code === AGREEMENT_REQUIREMENT_TYPES.SHOW);
		if(!requiresShowInteraction) {
			return;
		}

		const showPayload = {
			Requirement: { Code: AGREEMENT_REQUIREMENT_TYPES.SHOW },
			Interaction: { Code: AGREEMENT_INTERACTION_TYPES.DISMISS }
		};

		const result = await api.post(ACCOUNT_CAMPAIGN_INTERACT(campaignId, agreementId), showPayload);

		return result;
	};

	service.setAgreements = async(campaigns) => {
		service.agreements = ([...campaigns])
			.filter(c => c.IsActive === true)
			.filter(c => c.Purpose.Function.Code === "AGREEMENT")
			.sort((a, b) => (new Date(b.BeginInclusive)) - (new Date(a.BeginInclusive)))
			.map(a => {
				const currentVersion = campaignService.getCurrentLatestVersionForCampaign(a);
				const interactionsForVersion = campaignService.subjectInteractionsByVersionId[currentVersion.Id];

				return mapCampaignVersionToAgreement(currentVersion, interactionsForVersion);
			});

		areAllRequiredAgreementsAccepted();

		return service.agreements;
	};

	service.presentRequiredAgreements = async() => {
		let requiredAgreements = service.agreements.filter(a => a.IsRequired && !a.IsAccepted);

		while(requiredAgreements.length > 0) {
			const firstRequiredAgreement = requiredAgreements[0];

			await service.viewAgreement(firstRequiredAgreement.AgreementVersion.Id);
			try {
				// modal instance will affirm or reject which will update the list of service.agreements
				const modalInstance = $uibModal.open({
					component: "agreement-display-modal",
					size: "lg",
					backdrop: "static",
					keyboard: false,
					windowClass: "vesl-top",
					resolve: {
						acceptValue: () => true,
						agreement: () => firstRequiredAgreement
					}
				});

				const accepted = await modalInstance.result;
				if(accepted !== true) {
					return false;
				}
			} 
			catch(e) {
				return false;
			}
			requiredAgreements = service.agreements.filter(a => a.IsRequired && !a.IsAccepted);
		}

		return true;
	};

	function areAllRequiredAgreementsAccepted() {
		service.hasRequiredAgreements = service.agreements.some(agreement => agreement.IsRequired && !agreement.IsAccepted);
	}

	function getAgreementVersionById(agreementId) {
		const campaignId = campaignService.versionIdToCampaignMap[agreementId];
		if(!campaignId) {
			$logger.error("Could not find campaign for version id", { agreementId, campaigns: service.campaigns });
			return Promise.reject(new Error("Notice not found"));
		}

		const thisAgreement = service.agreements.find(a => a.AgreementVersion.Id === agreementId);
		if(!thisAgreement) {
			$logger.error("Could not find agreement for id", { agreementId, agreements: service.agreements });
			return Promise.reject(new Error("Agreement not found"));
		}

		return { thisAgreement, campaignId };
	}

	function markAgreementAccepted(agreementId) {
		const agreementIndex = service.agreements.findIndex(n => n.AgreementVersion.Id === agreementId);

		service.agreements = [...service.agreements];
		service.agreements[agreementIndex] = {
			...service.agreements[agreementIndex], 
			IsAccepted: true
		};
		areAllRequiredAgreementsAccepted();
	}

	campaignService.addOnCampaignsChanged(service.setAgreements);

	return service;
}

export default angular
	.module("vesl.services.agreement-service", [])
	.service("agreementService", AgreementService).name;